import { gql, useMutation, useQuery } from '@apollo/client'
import { useEffect } from 'react'

import {
  MyOwnedTicketDetailQuery,
  MyOwnedTicketDetailQueryVariables,
  PickupUserFragmentDoc,
  TicketStatusDetailFragmentDoc,
  UseEventPickupTicketsMutation,
  UseEventPickupTicketsMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { spreadUserItemsBySku } from '@/core/sku'
import { useAnalytics } from '@/lib/analytics'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { analytics } = useAnalytics()
  const { router } = useRouter<{
    eventId: string
    collectionId: string
    pickupUserId: string
  }>()
  const { eventId, collectionId, pickupUserId } = router.query
  const { data, loading } = useQuery<
    MyOwnedTicketDetailQuery,
    MyOwnedTicketDetailQueryVariables
  >(QUERY, {
    variables: {
      pickupUserId,
      eventId,
      collectionId,
    },
  })
  const { showError } = useMessage()

  const [mutate, { loading: submitting }] = useMutation<
    UseEventPickupTicketsMutation,
    UseEventPickupTicketsMutationVariables
  >(USE_EVENT_PICKUP_TICKETS)

  useEffect(() => {
    if (!loading && data?.pickupUser) return
    analytics?.track('impression_eventPickup', {
      applicationKey: config.applicationKey,
      eventId: router.query.eventId,
      collectionId: router.query.collectionId,
      status: data?.pickupUser.isExpired
        ? 'expired'
        : data?.pickupUser.isCompleted
        ? 'completed'
        : 'waitig',
    })
  }, [loading, data?.pickupUser])

  return {
    state: {
      loading,
      submitting,
      pickupUser: data?.pickupUser ?? null,
      items: spreadUserItemsBySku(
        data?.pickupUser
          ?.userItems as MyOwnedTicketDetailQuery['pickupUser']['userItems']
      ),
    },
    actions: {
      async onUseTicket() {
        const items = data?.pickupUser.ticketItemSerialsInput
        if (!items) return

        analytics?.track('click_useTicket', {
          applicationKey: config.applicationKey,
          eventId: data?.pickupUser.eventId,
          collectionId: data?.pickupUser.collectionId,
          asArtist: false,
        })

        await mutate({
          variables: {
            inputs: {
              eventId: router.query.eventId,
              collectionId: router.query.collectionId,
              pickupUserId: router.query.pickupUserId,
            },
          },
          refetchQueries: () => {
            return [
              {
                query: QUERY,
                variables: { pickupUserId, eventId, collectionId },
              },
            ]
          },
          awaitRefetchQueries: true,
        }).catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query MyOwnedTicketDetail(
    $pickupUserId: ID!
    $eventId: String!
    $collectionId: String!
  ) {
    pickupUser(
      pickupUserId: $pickupUserId
      eventId: $eventId
      collectionId: $collectionId
    ) {
      ...PickupUser
    }
  }
  ${PickupUserFragmentDoc}
`

const USE_EVENT_PICKUP_TICKETS = gql`
  mutation UseEventPickupTickets($inputs: UseEventPickupTicketsInput!) {
    useEventPickupTickets(inputs: $inputs) {
      id
      obtainedDate
      ticketStatus {
        ...TicketStatusDetail
      }
    }
  }
  ${TicketStatusDetailFragmentDoc}
`
